import { Container, ContainerModule, interfaces } from 'inversify';
import getDecorators from 'inversify-inject-decorators';
import { Platform } from 'src/environment';
import type { SubscriptionService } from '../services/SubscriptionService';
import { SubscriptionServiceImpl } from '../services/SubscriptionService';
import { TealiumAnalyticsService } from '../analytics/TealiumAnalyticsService';
import { AnalyticsService } from '../analytics/types';
import { NativeAnalyticsService } from '../analytics/NativeAnalyticsService';
import { WebAnalyticsService } from '../analytics/WebAnalyticsService';
import { appConfigFactory } from '../factories/appConfig.factory';
import { windowFactory } from '../factories/window.factory';
import {
  EventsManager,
  EventsManagerImpl,
} from '../utilities/events-manager.utility';
import type { NativeBridge } from '../utilities/native-bridge.utility';
import { NativeBridgeImpl } from '../utilities/native-bridge.utility';
import type { CredentialService } from '../services/credential-service';
import { CredentialServiceImpl } from '../services/credential-service';
import { StorageUtility } from '../utilities/storage.utility';
import { storageUtilityFactory } from '../utilities/storageUtilityFactory';
import { MonitorService } from '../services/monitoring/MonitorService';
import type { SentryService } from '../services/monitoring/SentryService';
import { SentryServiceImpl } from '../services/monitoring/SentryService';
import type { PageActionTimingService } from '../services/PageActionTimingService';
import { PageActionTimingServiceImpl } from '../services/PageActionTimingService';
import type { FeatureToggleService } from '../toggles';
import { FeatureToggleServiceImpl } from '../toggles';
import type { NotificationService } from '../services/NotificationService';
import { NotificationServiceImpl } from '../services/NotificationService';
import type { BrazeService } from '../services/BrazeService';
import { BrazeServiceImpl } from '../services/BrazeService';
import type { NativeUiService } from '../services/NativeUiService';
import { NativeUiServiceImpl } from '../services/NativeUiService';
import type { CampusService } from '../services';
import { CampusServiceImpl } from '../services/CampusService';
import type { GeocodeService } from '../services/geocode.service';
import { GeocodeServiceImpl } from '../services/geocode.service';
import type { InAuthService } from '../services/InAuthService';
import { InAuthServiceImpl } from '../services/InAuthService';
import type { GrubTelemetryService } from '../services/monitoring/GrubTelemetryService';
import { GrubTelemetryServiceImpl } from '../services/monitoring/GrubTelemetryService';
import { TYPES } from './types';

const container = new Container();

const global = new ContainerModule((bind: interfaces.Bind) => {
  bind<Window>(TYPES.Window).toConstantValue(windowFactory());
  bind<AppConfig>(TYPES.AppConfig).toDynamicValue(appConfigFactory);
  bind<NativeBridge>(TYPES.NativeBridge).toDynamicValue(() =>
    NativeBridgeImpl.getInstance(),
  );
  bind<EventsManager>(TYPES.EventsManager).toConstantValue(
    EventsManagerImpl.getInstance(),
  );
});

const analytics = new ContainerModule((bind: interfaces.Bind) => {
  bind<AnalyticsService>(TYPES.AnalyticsService)
    .to(WebAnalyticsService)
    .inSingletonScope()
    .whenTargetTagged('isNative', false);
  bind<AnalyticsService>(TYPES.AnalyticsService)
    .to(NativeAnalyticsService)
    .inSingletonScope()
    .whenTargetTagged('isNative', true);
  bind<TealiumAnalyticsService>(TYPES.TealiumAnalyticsService)
    .to(TealiumAnalyticsService)
    .inSingletonScope();

  bind<interfaces.Factory<AnalyticsService>>(
    TYPES.AnalyticsServiceFactory,
  ).toFactory<AnalyticsService>((context) => () => {
    const appConfig = context.container.get<AppConfig>(TYPES.AppConfig);
    const isNative = appConfig.platform !== Platform.WEB;

    return context.container.getTagged<AnalyticsService>(
      TYPES.AnalyticsService,
      'isNative',
      isNative,
    );
  });
});

const services = new ContainerModule((bind: interfaces.Bind) => {
  bind<NotificationService>(TYPES.NotificationService)
    .to(NotificationServiceImpl)
    .inSingletonScope();
  bind<CredentialService>(TYPES.CredentialsService).toDynamicValue(() =>
    CredentialServiceImpl.getInstance(),
  );
  bind<SubscriptionService>(TYPES.SubscriptionService)
    .to(SubscriptionServiceImpl)
    .inSingletonScope();
  bind<CampusService>(TYPES.CampusService)
    .to(CampusServiceImpl)
    .inSingletonScope();
  bind<FeatureToggleService>(TYPES.FeatureToggleService)
    .toDynamicValue(() => FeatureToggleServiceImpl.getInstance())
    .inSingletonScope();
  bind<BrazeService>(TYPES.BrazeService).to(BrazeServiceImpl);
  bind<InAuthService>(TYPES.InAuthService)
    .to(InAuthServiceImpl)
    .inSingletonScope();
  bind<NativeUiService>(TYPES.NativeUiService)
    .to(NativeUiServiceImpl)
    .inSingletonScope();
  bind<GeocodeService>(TYPES.GeocodeService)
    .to(GeocodeServiceImpl)
    .inSingletonScope();
  bind<PageActionTimingService>(TYPES.PageActionTimingService)
    .to(PageActionTimingServiceImpl)
    .inSingletonScope();
});

const monitorServices = new ContainerModule((bind: interfaces.Bind) => {
  bind<SentryService>(TYPES.SentryService)
    .to(SentryServiceImpl)
    .inSingletonScope();
  bind<GrubTelemetryService>(TYPES.GrubTelemetryService)
    .to(GrubTelemetryServiceImpl)
    .inSingletonScope();
  bind<MonitorService>(TYPES.MonitorService)
    .to(MonitorService)
    .inSingletonScope();
});

const utilities = new ContainerModule((bind: interfaces.Bind) => {
  bind<interfaces.Factory<StorageUtility>>(
    TYPES.StorageUtilityFactory,
  ).toFactory<StorageUtility>(() => storageUtilityFactory);
});

container.load(global, analytics, services, monitorServices, utilities);

const { lazyInject, lazyInjectNamed, lazyInjectTagged, lazyMultiInject } =
  getDecorators(container);

export {
  lazyInject,
  lazyInjectNamed,
  lazyInjectTagged,
  lazyMultiInject,
  container,
};
